import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { Send, User, Mail, MessageSquare, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Votre nom est requis").max(100),
  email: z.string().trim().email("Email invalide").max(255),
  plan: z.string().min(1, "Choisissez une formule"),
  message: z.string().trim().min(10, "Minimum 10 caractères").max(5000),
  website: z.string().optional(),
});

type FormData = z.infer<typeof contactSchema>;

const SellContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", plan: "", message: "", website: "" },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      // Insert order as prospect
      const { error: orderError } = await supabase
        .from("orders")
        .insert({
          name: data.name,
          email: data.email,
          plan: data.plan,
          message: data.message,
          status: "prospect" as const,
        });

      if (orderError) console.error("Order insert error:", orderError);

      // Send notification email to admin + confirmation to client
      const { data: response, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: data.name,
          email: data.email,
          subject: `Demande formule ${data.plan}`,
          message: `Formule choisie : ${data.plan}\n\n${data.message}`,
          website: data.website,
        },
      });

      if (error) throw new Error(error.message);
      if (response?.error) throw new Error(response.error);

      setSubmitStatus("success");
      analytics.formSubmit(`sell_contact_${data.plan}`);
      form.reset();
    } catch (error: any) {
      setSubmitStatus("error");
      setErrorMessage(error.message || "Une erreur est survenue");
      analytics.formError("sell_contact", error.message || "unknown");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">Demande envoyée !</h3>
        <p className="text-muted-foreground mb-6">
          Je reviens vers vous sous 24h avec tous les détails.
        </p>
        <Button variant="outline" onClick={() => setSubmitStatus("idle")}>
          Envoyer une autre demande
        </Button>
      </motion.div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-primary" /> Nom complet
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jean Dupont" className="bg-background/50 border-border/50" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-primary" /> Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jean@email.com" className="bg-background/50 border-border/50" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="plan"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm">Formule souhaitée</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-border/50">
                    <SelectValue placeholder="Choisir une formule" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Starter — 79€">Starter — 79€</SelectItem>
                  <SelectItem value="Clé en main — 149€">Clé en main — 149€</SelectItem>
                  <SelectItem value="Sur-mesure — 349€">Sur-mesure — 349€</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-sm">
                <MessageSquare className="w-4 h-4 text-primary" /> Votre projet
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Décrivez brièvement votre métier et ce que vous attendez de votre portfolio..."
                  className="bg-background/50 border-border/50 min-h-[120px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot */}
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
              <FormControl>
                <Input type="text" tabIndex={-1} autoComplete="off" {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        {submitStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-xl text-destructive"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{errorMessage}</p>
          </motion.div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          size="lg"
          className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground font-semibold py-6"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" /> Envoyer ma demande
            </>
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-3">
          En soumettant ce formulaire, vous acceptez notre{" "}
          <a href="/legal" className="text-primary hover:underline underline-offset-2">politique de confidentialité</a>.
        </p>
      </form>
    </Form>
  );
};

export default SellContactForm;
